'use client';

import type { ComponentProps } from 'react';
import { memo } from 'react';
import type { FixedThemes } from '@frameio/vapor';
import { VaporProvider } from './VaporProvider';

/**
 * @description a higher-order component that generates a static provider for the app theme context
 */
const createAppUiThemeStatic = (theme: FixedThemes) => {
  const Component = ({
    children,
    toastMargin = `12px`,
    ...otherProps
  }: ComponentProps<typeof VaporProvider>) => (
    <VaporProvider
      {...otherProps}
      defaultTheme={theme}
      toastMargin={toastMargin}
    >
      {children}
    </VaporProvider>
  );

  // set the display name for the component for debugging and testing
  Component.displayName = `AppUiThemeStatic${
    theme.charAt(0).toUpperCase() + theme.slice(1)
  }`;

  return memo(Component);
};

/**
 * @description static providers for the app theme context
 * @note this provider should be used at the root of the app and DOES NOT need an apollo provider
 * @example
 * ```tsx
 * <AppUiThemeStaticLight>
 *   <AppConsumingAppTheme />
 * </AppUiThemeStaticLight>
 * ```
 */
export const AppUiThemeStaticDark = createAppUiThemeStatic(`dark`);
export const AppUiThemeStaticLight = createAppUiThemeStatic(`light`);
